import React, { useState, useEffect } from 'react';
import { HorseRead, UserHorseCollectionRead, HorseStatus, Country } from '../types/api';
import { apiService } from '../services/api';
import { Eye, Calendar, XCircle } from 'lucide-react';
import { getHorseAvatarUrl } from '../utils/horseAvatar';
import { getFlagUrlByNameOrCode } from '../utils/countryFlag';
import { formatPersonName } from '../utils/personName';
import { formatHorseName } from '../utils/horseName';

interface HorseCardProps {
  horse: HorseRead;
  isInCollection?: boolean;
  collectionData?: UserHorseCollectionRead | null;
  onCollectionChange?: (horseId: string, added: boolean, collectionData?: UserHorseCollectionRead) => void;
  onExpandClick?: (horseId: string) => void;
  countries?: Country[];
}

export const HorseCard: React.FC<HorseCardProps> = ({ 
  horse, 
  isInCollection = false, 
  collectionData = null,
  onCollectionChange,
  onExpandClick,
  countries = [],
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inCollection, setInCollection] = useState(isInCollection);
  const [currentCollectionData, setCurrentCollectionData] = useState<UserHorseCollectionRead | null>(collectionData);
  const isPrivateHorse = currentCollectionData?.is_private ?? false;

  // Update state when props change
  useEffect(() => {
    setInCollection(isInCollection);
    setCurrentCollectionData(collectionData);
  }, [isInCollection, collectionData]);

  const handleRemoveFromCollection = async () => {
    if (!apiService.isAuthenticated()) {
      alert('Please log in to manage your collection');
      return;
    }

    setIsLoading(true);
    try {
      if (isPrivateHorse) {
        const confirmed = window.confirm('Deleting this private horse will remove it from your collection. Continue?');
        if (!confirmed) {
          setIsLoading(false);
          return;
        }
        await apiService.deletePrivateHorse(horse.unified_horse_key);
        window.dispatchEvent(new CustomEvent('collection:refresh'));
      } else {
        await apiService.removeFromCollection(horse.unified_horse_key);
        window.dispatchEvent(new CustomEvent('collection:refresh'));
      }
      setInCollection(false);
      setCurrentCollectionData(null);
      onCollectionChange?.(horse.unified_horse_key, false);
    } catch (error) {
      console.error('Error toggling collection:', error);
      alert('Failed to update collection');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: HorseStatus) => {
    if (!apiService.isAuthenticated()) {
      alert('Please log in to manage your collection');
      return;
    }

    setIsLoading(true);
    try {
      let result;
      if (!inCollection) {
        result = await apiService.addToCollection(horse.unified_horse_key, newStatus);
        setInCollection(true);
      } else {
        result = await apiService.updateCollectionStatus(horse.unified_horse_key, newStatus);
      }
      setCurrentCollectionData(result);
      onCollectionChange?.(horse.unified_horse_key, true, result);

      // Offer profile assignment for watch status (not skip)
      if (newStatus === 'watch') {
        window.dispatchEvent(new CustomEvent('profiles:assign', {
          detail: { horseId: horse.unified_horse_key, collectionItem: result },
        }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update horse status');
    } finally {
      setIsLoading(false);
    }
  };

  const formatYear = (year?: number | null) => {
    return year ? year.toString() : 'Unknown';
  };

  const uiStatus = currentCollectionData?.status === 'skip'
    ? 'skip'
    : currentCollectionData?.status === 'watch'
      ? 'watch'
      : null;
  const isWatching = uiStatus === 'watch';
  const isSkipping = uiStatus === 'skip';


  const handleWatchClick = async () => {
    if (isLoading) return;
    if (isWatching) {
      await handleRemoveFromCollection();
      return;
    }
    await handleStatusChange('watch');
  };

  const handleSkipClick = async () => {
    if (isLoading) return;
    if (isSkipping) {
      await handleRemoveFromCollection();
      return;
    }
    await handleStatusChange('skip');
  };

  return (
    <div
      className="relative overflow-visible bg-[#E7EEE3] rounded-[28px] shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 border border-gray-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 max-w-[320px] lg:max-w-none w-full ml-4 sm:ml-6"
      role="button"
      tabIndex={0}
      onClick={() => onExpandClick?.(horse.unified_horse_key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onExpandClick?.(horse.unified_horse_key);
        }
      }}
    >
      <div className="p-4 sm:p-5 pl-5 sm:pl-7">
        {/* Avatar (cut-out) */}
        <div className="absolute -left-12 sm:-left-14 top-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-white border-[3.5px] border-white flex items-center justify-center overflow-hidden shadow-md">
          <img
            src={getHorseAvatarUrl(horse.unified_sex, horse.unified_color)}
            alt={horse.unified_horse_name || 'Horse avatar'}
            className="w-full h-full object-contain"
          />
        </div>

        <div className="grid grid-cols-[1fr,auto] gap-4 items-center">
          {/* Details */}
          <div className="min-w-0 text-left">
            <h3 className="text-base font-semibold text-gray-900 leading-snug truncate">
              {formatHorseName(horse.unified_horse_name)}
            </h3>
            <div className="text-xs text-gray-600 flex items-center gap-1.5 mt-1 flex-wrap">
              {horse.unified_birth_year && (
                <span className="inline-flex items-center gap-1">
                  <Calendar size={14} className="text-gray-400" />
                  {formatYear(horse.unified_birth_year)}
                </span>
              )}
              {horse.unified_sex && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="inline-flex items-center gap-1 text-gray-700">
                    {horse.unified_sex}
                  </span>
                </>
              )}
              {horse.unified_color && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="inline-flex items-center gap-1 text-gray-700">
                    {horse.unified_color}
                  </span>
                </>
              )}
              {horse.horse_level !== null && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="inline-flex items-center gap-1 text-gray-900">
                    Level: {horse.horse_level}cm
                  </span>
                </>
              )}
              {horse.horse_country && getFlagUrlByNameOrCode(horse.horse_country, countries) && (
                <>
                  <span className="text-gray-400">•</span>
                  <img
                    src={getFlagUrlByNameOrCode(horse.horse_country, countries)!}
                    alt={horse.horse_country}
                    className="w-4 h-4 rounded-full"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </>
              )}
            </div>
            {(horse.unified_sire_name || horse.unified_dam_sire) && (
              <p className="text-xs text-gray-800 mt-1 truncate text-left">
                {(formatHorseName(horse.unified_sire_name) || '—') + ' × ' + (formatHorseName(horse.unified_dam_sire) || '—')}
              </p>
            )}
            {currentCollectionData?.profiles && currentCollectionData.profiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {currentCollectionData.profiles.map(profile => (
                  <span
                    key={profile.id}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#132B64]/10 text-[#132B64] border border-[#132B64]/20"
                  >
                    {profile.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 shrink-0 self-center ml-2">
            <button
              onClick={(e) => { e.stopPropagation(); handleWatchClick(); }}
              disabled={isLoading}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                isWatching
                  ? 'bg-[#132B64] text-white border-[#132B64] hover:bg-[#0f2350]'
                  : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Eye size={16} />
              {isWatching ? 'Watching' : 'Watch'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleSkipClick(); }}
              disabled={isLoading}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                isSkipping
                  ? 'bg-gray-100 text-gray-900 border-gray-200 hover:bg-gray-200'
                  : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <XCircle size={16} />
              {isSkipping ? 'Skipped' : 'Skip'}
            </button>
          </div>
          {/* Full width so it can run under the Watch/Skip buttons; -mt-3 offsets the grid gap */}
          {(horse.current_rider_name || horse.current_owner_name) && (
            <p className="col-span-2 -mt-3 min-w-0 text-xs text-gray-600 truncate text-left">
              {[
                horse.current_rider_name && `Rider: ${formatPersonName(horse.current_rider_name)}`,
                horse.current_owner_name && `Owner: ${formatPersonName(horse.current_owner_name)}`,
              ]
                .filter(Boolean)
                .join(' • ')}
            </p>
          )}
        </div>
        {/* Removed lower summary blocks to keep all info above actions */}
      </div>
    </div>
  );
}; 